import { Controller, useFormContext, get } from "react-hook-form";
import { cn } from "@/utils/cn";
import { cva } from "class-variance-authority";
import {
    Field,
    Label,
    Combobox as HeadlessCombobox,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
} from "@headlessui/react";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import React from "react";

const fieldVariants = cva("flex items-start", {
    variants: {
        labelPlacement: {
            start: "flex-row",
            end: "flex-row-reverse",
            top: "flex-col",
            bottom: "flex-col-reverse",
        },
        size: {
            default: "text-base",
            sm: "text-sm",
            lg: "text-lg",
        },
    },
    defaultVariants: {
        labelPlacement: "top",
        size: "default",
    },
});

const labelVariants = cva("mb-1 block w-full text-sm/6 font-medium", {
    variants: {
        variant: {
            primary: "text-darkgreen-500",
            success: "text-lime-700",
            danger: "text-red-500",
        },
        floatingLabel: {
            true: "pointer-events-none absolute z-1 mb-0 w-fit pt-2 pl-14 text-[0.6875rem]/6 tracking-widest",
            false: "",
        },
    },
    compoundVariants: [
        {
            variant: ["primary", "success", "danger"],
            hideLabel: true,
            className: "sr-only w-fit",
        },
    ],
    defaultVariants: {
        variant: "primary",
        hideLabel: false,
        floatingLabel: "false",
    },
});

const inputVariants = cva(
    "sm:text-md/6 block w-full min-w-0 appearance-none rounded-3xl bg-white px-10 py-3 text-base outline-2 -outline-offset-1 focus:outline-2 focus:-outline-offset-2 disabled:opacity-50 data-open:outline-2 data-open:-outline-offset-2",
    {
        variants: {
            variant: {
                primary:
                    "text-darkgreen-500 shadow-darkgreen-500/25 outline-neutral-200 placeholder:text-darkgreen-500 focus:outline-darkgreen-500/50 data-open:outline-darkgreen-500/50",
                success:
                    "text-lime-700 shadow-lime-500/25 outline-lime-500 placeholder:text-lime-700 focus:outline-lime-600",
                danger: "text-red-400 shadow-red-500/25 outline-red-500 placeholder:text-red-400 focus:outline-red-600",
                white: "text-darkgreen-500 shadow-darkgreen-200/25 outline-neutral-200 placeholder:text-darkgreen-500/75 focus:outline-darkgreen-500/50",
            },
            size: {
                default: "font-base rounded-3xl py-3 pr-12 pl-5 text-base",
                sm: "font-sm rounded-3xl py-2 pr-12 pl-4 text-sm",
                lg: "font-lg rounded-3xl py-4 pr-12 pl-8 text-lg",
            },
            iconPosition: {
                left: "pl-12",
                right: "pr-12",
            },
            floatingLabel: {
                true: "rounded-4xl pt-8 pb-4 pl-14!",
                false: "",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
            floatingLabel: "false",
            iconPosition: "none",
        },
    },
);

const chevronVariants = cva("size-4 stroke-[3]", {
    variants: {
        variant: {
            primary:
                "stroke-darkgreen-500/60 group-data-hover:stroke-darkgreen-500",
            success: "stroke-lime-700/60 group-data-hover:stroke-lime-700",
            danger: "stroke-red-500/60 group-data-hover:stroke-red-500",
        },
        size: {
            default: "",
            sm: "",
            lg: "",
        },
    },
    defaultVariants: {
        variant: "primary",
        size: "default",
    },
});

const optionsVariants = cva(
    "z-100000 mb-3 w-(--input-width) max-w-128! overflow-y-auto rounded-xl shadow-sm outline-2 transition duration-200 ease-out empty:invisible data-leave:data-closed:opacity-0 md:max-h-128! [&::-webkit-scrollbar]:[width:6px] [&::-webkit-scrollbar-thumb]:[border-radius:3px] [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-track]:[margin:8px_0] [&>div]:mx-1.5 [&>div]:first:mt-1.5 [&>div]:last:mb-1.5",
    {
        variants: {
            variant: {
                primary: "bg-darkgreen-500",
                success: "bg-lime-500",
                danger: "bg-red-500",
                white: "bg-white outline-gray-200",
            },
            size: {
                default:
                    "[--anchor-gap:--spacing(1)] [--anchor-padding:--spacing(1)]",
                sm: "[--anchor-gap:--spacing(1)] [--anchor-padding:--spacing(1)]",
                lg: "[--anchor-gap:--spacing(1)] [--anchor-padding:--spacing(1)]",
            },
            floatingLabel: {
                true: "[&>div:not([role=option])]:text-[0.6875rem]/6 [&>div:not([role=option])]:tracking-widest [&>div:not([role=option])]:uppercase",
                false: "",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
            floatingLabel: "false",
        },
    },
);

const optionVariants = cva(
    "group w-[calc(100% - 12px)] flex cursor-default items-center gap-2 rounded-lg py-1.5 select-none",
    {
        variants: {
            variant: {
                primary:
                    "text-white data-focus:bg-darkgreen-400 [&>svg]:stroke-white",
                success:
                    "text-black data-focus:bg-lime-600 [&>svg]:stroke-black",
                danger: "text-white data-focus:bg-red-600 [&>svg]:stroke-white",
                white: "text-darkgreen-500 data-focus:bg-black/5 [&>svg]:stroke-darkgreen-500",
            },
            size: {
                default: "px-3",
                sm: "px-2",
                lg: "px-3",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
        },
    },
);

const iconVariants = cva(
    "pointer-events-none absolute top-1/2 shrink-0 -translate-y-1/2 stroke-2",
    {
        variants: {
            variant: {
                primary: "text-darkgreen-500",
                success: "text-lime-700",
                danger: "text-red-400",
            },
            size: {
                default: "size-5",
                sm: "size-4",
                lg: "size-6",
            },
            iconPosition: {
                left: "left-5",
                right: "right-5",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
            iconPosition: "none",
        },
    },
);

export const Combobox = ({
    label,
    placeholder,
    name,
    className,
    disabled,
    multiple = false,
    hideLabel,
    variant,
    labelPlacement,
    size,
    children,
    icon: Icon,
    iconPosition,
    floatingLabel = false,
    onChange,
    hideCheck = false,
}) => {
    const options = React.Children.toArray(children).reduce(
        (result, child, index) => {
            if (!React.isValidElement(child)) return result;

            if (child.type === ComboboxHeader) {
                result.push({
                    type: "divider",
                    key: `divider-${index}`,
                    label: child.props.children,
                    disabled: child.props.disabled,
                });

                return result;
            }

            if (child.type === "option") {
                const group = [...result]
                    .reverse()
                    .find((item) => item.type === "divider")?.key;

                result.push({
                    type: "option",
                    value: child.props.value,
                    label: child.props.children,
                    disabled: child.props.disabled,
                    group,
                });
            }

            return result;
        },
        [],
    );

    const [query, setQuery] = useState("");

    const filteredOptions =
        query === ""
            ? options
            : options.filter((opt) => {
                  if (opt.type === "option") {
                      return String(opt.label)
                          .toLowerCase()
                          .includes(query.toLowerCase());
                  }

                  return options.some(
                      (o) =>
                          o.type === "option" &&
                          o.group === opt.key &&
                          String(o.label)
                              .toLowerCase()
                              .includes(query.toLowerCase()),
                  );
              });

    const {
        control,
        formState: { errors /*isSubmitted*/ },
    } = useFormContext();

    const hasError = get(errors, name);
    const formStateVariant = hasError
        ? "danger"
        : /*: isSubmitted
          ? "success"*/
          variant || "primary";

    const iconDefaultPosition = Icon ? (iconPosition ?? "left") : undefined;

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={multiple ? [] : ""}
            render={({ field }) => (
                <Field
                    disabled={disabled}
                    className={cn(fieldVariants({ labelPlacement, size }))}
                >
                    <Label
                        className={cn(
                            labelVariants({
                                variant: formStateVariant,
                                hideLabel,
                                floatingLabel,
                            }),
                        )}
                    >
                        {label}
                    </Label>
                    <div className="w-full">
                        <HeadlessCombobox
                            value={
                                multiple
                                    ? Array.isArray(field.value)
                                        ? field.value
                                        : []
                                    : (field.value ?? "")
                            }
                            onChange={(value) => {
                                field.onChange(value);
                                onChange?.(value);
                            }}
                            onClose={() => setQuery("")}
                            multiple={multiple}
                            immediate
                        >
                            <div className="relative flex">
                                {Icon && (
                                    <Icon
                                        className={cn(
                                            iconVariants({
                                                variant: formStateVariant,
                                                size,
                                                iconPosition:
                                                    iconDefaultPosition,
                                            }),
                                        )}
                                    />
                                )}
                                <ComboboxInput
                                    className={cn(
                                        inputVariants({
                                            className,
                                            variant: formStateVariant,
                                            size,
                                            iconPosition: iconDefaultPosition,
                                            floatingLabel,
                                        }),
                                    )}
                                    displayValue={(value) => {
                                        if (multiple) {
                                            if (!Array.isArray(value))
                                                return "";
                                            return options
                                                .filter((o) =>
                                                    value.includes(o.value),
                                                )
                                                .map((o) => o.label)
                                                .join(", ");
                                        }

                                        return (
                                            options.find(
                                                (o) => o.value === value,
                                            )?.label || ""
                                        );
                                    }}
                                    placeholder={placeholder}
                                    onChange={(e) => setQuery(e.target.value)}
                                    autoComplete="off"
                                />
                                <ComboboxButton className="group absolute inset-y-0 right-0 px-5">
                                    <ChevronDownIcon
                                        className={cn(
                                            chevronVariants({
                                                variant: formStateVariant,
                                                hideLabel,
                                            }),
                                        )}
                                    />
                                </ComboboxButton>
                            </div>

                            <ComboboxOptions
                                anchor="bottom start"
                                transition
                                className={cn(
                                    optionsVariants({
                                        variant: formStateVariant,
                                        size,
                                        floatingLabel,
                                    }),
                                )}
                            >
                                {filteredOptions.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                        No results found
                                    </div>
                                ) : (
                                    filteredOptions.map((option) =>
                                        option.type === "divider" ? (
                                            <div
                                                key={option.key}
                                                className="mx-3! mt-4! mb-2! text-sm font-medium"
                                            >
                                                {option.label}
                                            </div>
                                        ) : (
                                            <ComboboxOption
                                                key={option.value}
                                                value={option.value}
                                                disabled={option.disabled}
                                                className={cn(
                                                    optionVariants({
                                                        variant:
                                                            formStateVariant,
                                                        size,
                                                    }),
                                                )}
                                            >
                                                {!hideCheck && (
                                                    <CheckIcon className="invisible size-4 shrink-0 stroke-3 group-data-selected:visible" />
                                                )}
                                                <div className="text-sm/6">
                                                    {option.label}
                                                </div>
                                            </ComboboxOption>
                                        ),
                                    )
                                )}
                            </ComboboxOptions>
                        </HeadlessCombobox>
                        {hasError && (
                            <div className="mt-2">
                                <InputError
                                    message={hasError.message}
                                    key={hasError.message}
                                />
                            </div>
                        )}
                    </div>
                </Field>
            )}
        />
    );
};

const InputError = ({ message }) => {
    return (
        <div className="px-4">
            <span role="alert" className="text-red-400">
                {message}
            </span>
        </div>
    );
};

export function ComboboxHeader() {
    return null;
}

Combobox.Header = ComboboxHeader;
